import ky, { type AfterResponseHook, type BeforeRequestHook, type Options } from 'ky'
import config, { hasZendeskProxy } from '@/config'
import i18n from '@/i18n'
import { bus } from '@/utils/bus'
import { session } from '@/composables/useSession'
import { getAccessToken, logout } from '@/modules/auth/api'
import { includesHandledValidationError } from '@/modules/settings/components/ai/utils'

/**
 * APIError used for logging
 */
export class APIError<TBody = string> extends Error {
  body: TBody
  status: number
  traceContext: string | null

  constructor(response: Response, body: TBody) {
    const url = response.url.replace(window.location.origin, '')
    super(`${response.status} ${url}`)
    this.name = 'APIError'
    this.body = body
    this.status = response.status
    this.traceContext = response.headers.get('X-Cloud-Trace-Context')
  }
}

export const getErrorResponseText = (err: { data?: unknown; response?: Response }): Promise<string> => {
  if (err.data !== undefined) {
    return Promise.resolve(typeof err.data === 'string' ? err.data : JSON.stringify(err.data))
  }

  return err.response?.text() ?? Promise.resolve('')
}

const accessTokenHook: BeforeRequestHook = async ({ request }) => {
  if (!hasZendeskProxy) {
    const accessToken = await getAccessToken()
    request.headers.set('Authorization', `Bearer ${accessToken}`)
  }

  if (session.account) request.headers.set('X-Klaus-Account', session.account.id.toString())
}

const ML_VALIDATION_ENDPOINTS = ['autoqa/ai-insights/validate', 'autoqa/ai-insights/prompt-enhance']

export const errorHandlerHook: AfterResponseHook = async ({ request, response }) => {
  let res = await (response.headers.get('content-type') === 'application/json'
    ? response.clone().json()
    : response.clone().text())
  if (typeof res === 'string') res = { error: res.trim() }

  const traceContext = response.headers.get('X-Cloud-Trace-Context')

  switch (response.status) {
    case 401:
      // do not show error popup for session 401's
      if (request.url.includes('users/session')) break
      bus.$emit('universal-error', {
        error: i18n.t('universal.errors.401_v2'),
        request,
        response: res,
        ref: traceContext,
      })
      break
    case 400:
    case 404:
    case 406:
      // Do not show error popup for ML Validation failure if it's handled in the UI
      if (
        ML_VALIDATION_ENDPOINTS.some((endpoint) => request.url.includes(endpoint)) &&
        includesHandledValidationError(res.message)
      )
        break

      // do not show error popup for ML Validation failure for prompt categories to handle error in the UI
      if (request.url.includes('settings/reviews/categories') && res.message.includes('ML Validation failure')) break

      // do not show error popup for duplicate tier/subtier names - handled in the UI
      if (
        request.url.includes('settings/reviews/root-causes/tiers') &&
        res.code === 3 &&
        res.message === 'tier already exists'
      )
        break

      // do not show error popup for duplicate root cause names - handled in the UI
      if (
        request.url.includes('settings/reviews/root-causes') &&
        res.code === 3 &&
        res.message === 'root cause with the same name already exists under this tier'
      )
        break

      bus.$emit('universal-error', { ...res, response: res, request, ref: traceContext })
      break
    case 409:
      // do not show error popup for global account lock screen
      if (request.url.includes('users/session')) break
      bus.$emit('universal-error', {
        title: i18n.t('provisioning.errors.account_locked.title'),
        message: i18n.t('provisioning.errors.account_locked.body', {
          link: i18n.t('provisioning.errors.account_locked.try_again'),
        }),
        warning: true,
      })
      break
    case 429:
      bus.$emit('universal-error', {
        error: i18n.t('universal.errors.slow_down'),
        request,
        response: res,
        ref: traceContext,
      })
      break
    case 500:
      bus.$emit('universal-error', {
        error: i18n.t('universal.errors.500'),
        ref: traceContext || 'internal-error',
        status: response.status,
        request,
        response: res,
      })
      break
  }

  if (res.errorCode === 'payment_authentication_method_disabled') {
    const { sub } = session.idTokenPayload
    const method = sub.substring(0, sub.lastIndexOf('|'))
    return logout({ disabledAuthMethod: method })
  }

  if (res.errorCode === 'technical_exception') {
    bus.$emit('universal-error', { ...res, response: res, request, ref: traceContext })
  } else if (res.errorCode === 'payment_user_limit_reached') {
    bus.$emit('user-limit-reached')
  }

  if (!response.ok) {
    const body = await response.clone().text()
    throw new APIError(response, body)
  }
}

export const baseApi = (options?: Options) =>
  ky.extend({
    timeout: 120 * 1000,
    retry: 0,
    hooks: {
      beforeRequest: window.Cypress?.testingType === 'component' ? [] : [accessTokenHook],
      afterResponse: [errorHandlerHook],
    },
    ...options,
  })

export const prefixUrl = config.apiOverride ? `${config.apiOverride}/api` : `${config.baseUrl}/api`

export const api = baseApi({ prefix: prefixUrl })
export const zendeskApi = baseApi({ prefix: '/api' })
export const timeTrackingApi = baseApi({ prefix: prefixUrl, retry: { limit: 3, methods: ['post'] } })

export const oldApiPrefixUrl = config.apiOverride ? `${config.apiOverride}/old-api` : `${config.baseUrl}/old-api`
export const oldApi = baseApi({ prefix: oldApiPrefixUrl })
